import { mapUrlKeys } from '../../utils';
import { serializeTableSettings, readColumnStateFromLocalStorage } from './tableSettings';
import { serializeFilterCriteria, deserializeFilterCriteria } from './filterCriteriaEncoding';

const VIEW_CRITERIA_MAX_LENGTH = 4000;

const RESERVED_KEYS = new Set(['_ts', '_fc', '_s', '_o']);

/**
 * Decodes a saved view's filterCriteria onto a URLSearchParams instance.
 *
 * Handles both the compact encoding (_fc/_s/_o keys) and the legacy
 * format where filter values were stored as parsed JS objects. Column layout
 * (`_ts`) is NOT applied here — the Table component applies it via React
 * state setters when it receives the view through `onViewChange`, so that
 * `useLocalStorageState` consumers stay in sync.
 */
export const applyViewCriteriaToParams = (
  filterCriteria: Record<string, unknown>,
  _tableName: string,
  params: URLSearchParams
): void => {
  const criteria: Record<string, unknown> = { ...filterCriteria };
  const compactFc = criteria._fc;
  const compactSort = criteria._s;
  const compactOrder = criteria._o;

  const remainingCriteria: Record<string, unknown> = {};
  Object.entries(criteria).forEach(([key, value]) => {
    if (!RESERVED_KEYS.has(key)) {
      remainingCriteria[key] = value;
    }
  });

  // Restore filters: prefer compact _fc encoding, fall back to legacy object
  const filtersToApply: Record<string, string> =
    typeof compactFc === 'string'
      ? deserializeFilterCriteria(compactFc)
      : (() => {
          const mapped = mapUrlKeys(remainingCriteria);
          const result: Record<string, string> = {};
          Object.entries(mapped).forEach(([key, value]) => {
            const stringValue =
              value === null || value === undefined || value === ''
                ? ''
                : typeof value === 'number'
                  ? `n|${value}`
                  : typeof value === 'object'
                    ? (() => {
                        try {
                          return `o|${JSON.stringify(value)}`;
                        } catch {
                          return '';
                        }
                      })()
                    : String(value);
            if (stringValue !== '') {
              result[key] = stringValue;
            }
          });
          return result;
        })();

  Object.entries(filtersToApply).forEach(([key, value]) => {
    if (value !== '') {
      params.set(key, value);
    }
  });

  // Restore sort/order from dedicated keys (new format) or legacy criteria
  const sortValue =
    typeof compactSort === 'string' && compactSort
      ? compactSort
      : typeof remainingCriteria.sort === 'string'
        ? remainingCriteria.sort
        : null;
  const orderValue =
    typeof compactOrder === 'string' && compactOrder
      ? compactOrder
      : typeof remainingCriteria.order === 'string'
        ? remainingCriteria.order
        : null;

  if (sortValue) params.set('sort', sortValue);
  if (orderValue) params.set('order', orderValue);
};

/**
 * Returns true when the current URL filter state differs from the saved view's
 * filterCriteria, indicating unsaved changes.
 *
 * Uses fast string comparison for new-format views (_fc/_s/_o keys) and falls
 * back to deep object comparison for legacy views.
 */
export const hasViewFilterChanges = (
  currentFilters: Record<string, unknown>,
  savedCriteria: Record<string, unknown>
): boolean => {
  const currentFc = serializeFilterCriteria(currentFilters);

  const isCompactFormat =
    typeof savedCriteria._fc === 'string' ||
    '_s' in savedCriteria ||
    '_o' in savedCriteria ||
    '_ts' in savedCriteria;

  if (isCompactFormat) {
    const savedFc = typeof savedCriteria._fc === 'string' ? savedCriteria._fc : '';
    const currentSort = typeof currentFilters.sort === 'string' ? currentFilters.sort : '';
    const currentOrder = typeof currentFilters.order === 'string' ? currentFilters.order : '';
    const savedSort = typeof savedCriteria._s === 'string' ? savedCriteria._s : '';
    const savedOrder = typeof savedCriteria._o === 'string' ? savedCriteria._o : '';
    return currentFc !== savedFc || currentSort !== savedSort || currentOrder !== savedOrder;
  }

  // Legacy: compare parsed filter objects (views saved before compact encoding)
  const strippedSavedCriteria = Object.fromEntries(
    Object.entries(savedCriteria).filter(([key]) => !RESERVED_KEYS.has(key))
  );
  const normalizedCurrentFilters = mapUrlKeys(currentFilters);
  const normalizedViewFilters = mapUrlKeys(strippedSavedCriteria);

  const currentFilterKeys = Object.keys(normalizedCurrentFilters).sort();
  const viewFilterKeys = Object.keys(normalizedViewFilters).sort();

  if (currentFilterKeys.length !== viewFilterKeys.length) return true;
  if (currentFilterKeys.some((key, index) => key !== viewFilterKeys[index])) return true;

  const changes: Record<string, { current: unknown; saved: unknown }> = {};
  currentFilterKeys.forEach(key => {
    const currentValue = normalizedCurrentFilters[key];
    const viewValue = normalizedViewFilters[key];

    if (currentValue === null || currentValue === undefined) {
      if (viewValue !== null && viewValue !== undefined) {
        changes[key] = { current: currentValue, saved: viewValue };
      }
      return;
    }
    if (viewValue === null || viewValue === undefined) {
      if (currentValue !== null && currentValue !== undefined) {
        changes[key] = { current: currentValue, saved: viewValue };
      }
      return;
    }

    if (typeof currentValue === 'object' && typeof viewValue === 'object') {
      try {
        if (JSON.stringify(currentValue) !== JSON.stringify(viewValue)) {
          changes[key] = { current: currentValue, saved: viewValue };
        }
      } catch {
        changes[key] = { current: currentValue, saved: viewValue };
      }
      return;
    }

    if (currentValue !== viewValue) {
      changes[key] = { current: currentValue, saved: viewValue };
    }
  });

  return Object.keys(changes).length > 0;
};

/**
 * Builds the compact filterCriteria object to persist with a view, encoding
 * filters as _fc, column layout as _ts, and sort/order as _s/_o.
 */
export const buildViewFilterCriteria = (
  filters: Record<string, unknown>,
  tableName: string
): Record<string, unknown> => {
  const compactFc = serializeFilterCriteria(filters);
  const compactTs = serializeTableSettings(readColumnStateFromLocalStorage(tableName));

  const filterCriteria: Record<string, unknown> = {};
  if (compactFc) filterCriteria._fc = compactFc;
  if (compactTs) filterCriteria._ts = compactTs;
  if (typeof filters.sort === 'string' && filters.sort) filterCriteria._s = filters.sort;
  if (typeof filters.order === 'string' && filters.order) filterCriteria._o = filters.order;
  return filterCriteria;
};

/**
 * Returns true when the serialized filterCriteria exceeds the database column limit.
 */
export const isViewFilterCriteriaTooLarge = (filterCriteria: Record<string, unknown>): boolean => {
  try {
    return JSON.stringify(filterCriteria).length > VIEW_CRITERIA_MAX_LENGTH;
  } catch {
    return true;
  }
};
